import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { OutreachPlan } from "@hsh/shared";

type PlanStatus = "active" | "completed" | "archived";

interface PlanWithProgress
  extends Omit<OutreachPlan, "durationDays" | "progress"> {
  progress?: {
    totalPlaces: number;
    contacted: number;
    meetingsBooked?: number;
    referred?: number;
    notSuitable?: number;
    percentage: number;
  };
  durationDays?: number;
  status?: PlanStatus;
}

interface DeleteConfirmModalProps {
  plan: PlanWithProgress;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

function DeleteConfirmModal({
  plan,
  onConfirm,
  onCancel,
  isDeleting,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Plan</h3>
            <p className="text-sm text-gray-500">
              This action cannot be undone
            </p>
          </div>
        </div>

        <p className="text-gray-600 mb-6">
          Are you sure you want to delete{" "}
          <span className="font-medium text-gray-900">"{plan.name}"</span>? This
          will permanently remove the plan and its progress data.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? "Deleting..." : "Delete Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_TABS: { value: PlanStatus | "all"; label: string }[] = [
  { value: "all", label: "All Plans" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

function getPlanStatus(plan: PlanWithProgress): PlanStatus {
  const now = new Date();
  const endDate = new Date(plan.endDate);

  if (plan.status === "archived") return "archived";
  if (endDate < now) return "completed";
  return "active";
}

function getDaysRemaining(plan: PlanWithProgress): number {
  const now = new Date();
  const endDate = new Date(plan.endDate);
  const diffTime = endDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function getStatusBadgeColor(status: PlanStatus): string {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-700";
    case "completed":
      return "bg-blue-100 text-blue-700";
    case "archived":
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function PlansPage() {
  const [plans, setPlans] = useState<PlanWithProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PlanStatus | "all">("all");
  const [planToDelete, setPlanToDelete] = useState<PlanWithProgress | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setIsLoading(true);
    try {
      const response = await api.getPlans();
      setPlans(response.data || []);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!planToDelete?._id) return;

    setIsDeleting(true);
    try {
      await api.deletePlan(planToDelete._id);
      setPlans(plans.filter((p) => p._id !== planToDelete._id));
      setPlanToDelete(null);
    } catch (error) {
      console.error("Failed to delete plan:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredPlans = plans.filter((plan) => {
    if (activeTab === "all") return true;
    return getPlanStatus(plan) === activeTab;
  });

  const sortedPlans = [...filteredPlans].sort((a, b) => {
    // Active plans first, then by start date descending
    const statusA = getPlanStatus(a);
    const statusB = getPlanStatus(b);
    if (statusA === "active" && statusB !== "active") return -1;
    if (statusA !== "active" && statusB === "active") return 1;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  const getPlanCounts = () => {
    return {
      all: plans.length,
      active: plans.filter((p) => getPlanStatus(p) === "active").length,
      completed: plans.filter((p) => getPlanStatus(p) === "completed").length,
      archived: plans.filter((p) => getPlanStatus(p) === "archived").length,
    };
  };

  const counts = getPlanCounts();

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto pb-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Outreach Plans
          </h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Track and manage your business outreach campaigns
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4">
            <div className="text-xs md:text-sm text-gray-500">Total Plans</div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">
              {counts.all}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4">
            <div className="text-xs md:text-sm text-gray-500">Active</div>
            <div className="text-xl md:text-2xl font-bold text-green-600">
              {counts.active}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4">
            <div className="text-xs md:text-sm text-gray-500">Completed</div>
            <div className="text-xl md:text-2xl font-bold text-blue-600">
              {counts.completed}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4">
            <div className="text-xs md:text-sm text-gray-500">Archived</div>
            <div className="text-xl md:text-2xl font-bold text-gray-600">
              {counts.archived}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-4 md:mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`flex-1 px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.value
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                  <span className="ml-1 md:ml-2 px-1.5 md:px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                    {counts[tab.value as keyof typeof counts]}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Plans List */}
          <div className="divide-y divide-gray-100">
            {sortedPlans.length === 0 ? (
              <div className="p-12 text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  No plans found
                </h3>
                <p className="text-gray-500 mb-4">
                  {activeTab === "all"
                    ? "Create your first outreach plan from the map view"
                    : `No ${activeTab} plans`}
                </p>
                <Link
                  to="/map"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                >
                  Go to Map
                </Link>
              </div>
            ) : (
              sortedPlans.map((plan) => {
                const status = getPlanStatus(plan);
                const daysRemaining = getDaysRemaining(plan);
                const progress = plan.progress || {
                  totalPlaces: plan.targetCount,
                  contacted: 0,
                  percentage: 0,
                };

                return (
                  <div
                    key={plan._id}
                    className="p-4 md:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <Link to={`/plan/${plan._id}`} className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 truncate">
                            {plan.name}
                          </h3>
                          <span
                            className={`px-2 md:px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                              status
                            )}`}
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </div>
                        {plan.description && (
                          <p className="text-gray-600 text-sm mb-2 md:mb-3 line-clamp-2">
                            {plan.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-4 h-4 hidden md:block"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="md:hidden">
                              {new Date(plan.startDate).toLocaleDateString(
                                "en-GB",
                                { day: "numeric", month: "short" }
                              )}
                            </span>
                            <span className="hidden md:inline">
                              {new Date(plan.startDate).toLocaleDateString()} -{" "}
                              {new Date(plan.endDate).toLocaleDateString()}
                            </span>
                          </span>
                          <span className="hidden md:inline">•</span>
                          <span>
                            {Math.ceil((plan.durationDays || 56) / 7)} weeks
                          </span>
                          <span>•</span>
                          <span>{plan.targetCount} businesses</span>
                          {status === "active" && daysRemaining > 0 && (
                            <>
                              <span className="hidden md:inline">•</span>
                              <span className="text-primary-600 font-medium">
                                {Math.ceil(daysRemaining / 7)}w left
                              </span>
                            </>
                          )}
                        </div>
                      </Link>
                      <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4 md:ml-6">
                        {/* Progress Circle */}
                        <div className="relative w-14 h-14 md:w-16 md:h-16">
                          <svg className="w-14 h-14 md:w-16 md:h-16 transform -rotate-90">
                            <circle
                              cx="50%"
                              cy="50%"
                              r="24"
                              stroke="currentColor"
                              strokeWidth="5"
                              fill="transparent"
                              className="text-gray-200"
                            />
                            <circle
                              cx="50%"
                              cy="50%"
                              r="24"
                              stroke="currentColor"
                              strokeWidth="5"
                              fill="transparent"
                              strokeDasharray={`${
                                progress.percentage * 1.51
                              } 151`}
                              className="text-primary-500"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs md:text-sm font-semibold text-gray-900">
                              {progress.percentage}%
                            </span>
                          </div>
                        </div>
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPlanToDelete(plan);
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete plan"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                        <Link
                          to={`/plan/${plan._id}`}
                          className="hidden md:block"
                        >
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3 md:mt-4">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>
                          {progress.contacted} / {progress.totalPlaces}{" "}
                          contacted
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2">
                        <div
                          className="bg-primary-500 h-1.5 md:h-2 rounded-full transition-all duration-500"
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {planToDelete && (
        <DeleteConfirmModal
          plan={planToDelete}
          onConfirm={handleDeletePlan}
          onCancel={() => setPlanToDelete(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}
